
import Vue from "vue";
import TheTemplate from "../TheTemplate.vue";
import VueApexCharts from "vue-apexcharts";
import dayjs from "dayjs";

import BaseSelectorForm from "@/components/BaseSelectorForm.vue";

import { Box, Toilet, ToiletType } from "@/models";
import { NewToiletsApiFunc, NewBoxesApiFunc } from "@/api";
import {
  fetchBarGraphAnalyticsDataByRoomId,
  fetchBarGraphAverageAnalyticsDataByAreaId,
  fetchHeatmapAverageAnalyticsDataByAreaId,
  fetchHeatmapAnalyticsData,
  fetchStayTimeAnalyticsData,
  StaytimeSeriesList,
  fetchAnalyticsDataFormattedOnCsv,
  fetchAnalyticsDataByAreaIdFormattedOnCsv,
} from "@/api/analytics-new";
import { BarGraphAnalyticsData, HeatmapAnalyticsData } from "@/api/analytics-new";
import { ApexOptions } from "apexcharts";

type BarGraphAnalyticsDataInTheArea = {
  roomId: string;
  analyticsData: BarGraphAnalyticsData;
}[];
type HeatmapAnalyticsDataInTheArea = {
  roomId: string;
  analyticsData: HeatmapAnalyticsData;
}[];

const emptyHeatmapAnalyticsData: HeatmapAnalyticsData = [
  { name: "日", data: [] },
  { name: "土", data: [] },
  { name: "金", data: [] },
  { name: "木", data: [] },
  { name: "水", data: [] },
  { name: "火", data: [] },
  { name: "月", data: [] },
];
const renderingDelayTime = 0; // いろいろ実験したけど、何秒にしても体感あんまりかわらない気がする...

type CsvDownloadOption = "selectedAreaOnly" | "allArea";

type DataType = {
  selectedAreaId: string;
  selectedArea: Toilet | undefined;
  csvSelector: { name: string; value: CsvDownloadOption }[];
  areaList: Toilet[];
  roomList: Box[];
  barGraphAnalyticsAverageDataInTheArea: BarGraphAnalyticsData;
  barGraphAnalyticsDataInTheArea: BarGraphAnalyticsDataInTheArea;
  heatmapAnalyticsAverageDataInTheArea: HeatmapAnalyticsData;
  heatmapAnalyticsDataInTheArea: HeatmapAnalyticsDataInTheArea;
  baseBarGraphOptions: ApexOptions;
  baseHeatmapOptions: ApexOptions;
  staytimeChartOptions: ApexOptions;
  staytimeSeriesList: StaytimeSeriesList;
};

export default Vue.extend({
  name: "views-admin-analytics",
  components: {
    TheTemplate,
    VueApexCharts,
    BaseSelectorForm,
  },
  data(): DataType {
    return {
      selectedAreaId: "",
      selectedArea: undefined,
      csvSelector: [
        { name: "選択中のエリア", value: "selectedAreaOnly" },
        { name: "全エリア", value: "allArea" },
      ],
      areaList: [],
      roomList: [],
      barGraphAnalyticsAverageDataInTheArea: [],
      barGraphAnalyticsDataInTheArea: [],
      heatmapAnalyticsAverageDataInTheArea: [],
      heatmapAnalyticsDataInTheArea: [],
      baseBarGraphOptions: {
        chart: {
          // animzations: { enabled: false },
          toolbar: { tools: { download: false } },
          type: "bar",
          fontFamily: "Helvetica, Arial, sans-serif",
        },
        // markers: { size: 10 },
        colors: ["#7800d2"],
        dataLabels: {
          enabled: false,
        },
        tooltip: {
          x: { formatter: (unixtime: number) => dayjs(unixtime * 1000).format("M/D H:mm") },
          y: { formatter: (percentage: number) => `${percentage}%` },
        },
        xaxis: {
          type: "datetime",
          labels: { formatter: (_: string, unixtime: number) => dayjs(unixtime * 1000).format("M/D H:00") },
        },
        yaxis: { min: 0, max: 100, labels: { formatter: (tick: number) => `${tick.toFixed(0)}%` } },
        subtitle: {
          text: "使用率",
          align: "left",
        },
      },
      baseHeatmapOptions: {
        chart: { toolbar: { tools: { download: false } }, type: "heatmap", fontFamily: "Helvetica, Arial, sans-serif" },
        colors: ["#ee2039"],
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (percentage: number) => `${percentage}%` } },
        yaxis: { min: 0, max: 100 },
        grid: { position: "front" },
        subtitle: {
          text: "使用率",
          align: "left",
        },
      },
      staytimeChartOptions: {
        chart: { toolbar: { show: false }, type: "area", fontFamily: "Helvetica, Arial, sans-serif" },
        title: {
          text: "滞在時間別 - 滞在人数",
          align: "center",
        },
        colors: [
          "#55e0e8",
          "#1eb2c0",
          "#008892",
          "#bb86fc",
          "#620dee",
          "#3700b3",
          "#da4d7f",
          "#a01546",
          "#780224",
          "#87acff",
          "#426fff",
          "#002ed7",
          "#ffc441",
          "#f6a623",
          "#c37900",
        ],
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: [
            "0分〜1分",
            "1分〜2分",
            "2分〜4分",
            "4分〜6分",
            "6分〜8分",
            "8分〜10分",
            "10分〜15分",
            "15分〜20分",
            "20分〜30分",
            "30分以上",
          ],
        },
        yaxis: {
          labels: {
            formatter: function (value: number) {
              return value + "人";
            },
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "center",
        },
      },
      staytimeSeriesList: [],
    };
  },
  computed: {
    areaListForSelector(): { name: string; value: string }[] {
      return this.areaList.map((x) => {
        const floor = x.floor === undefined ? "" : `【${x.floor}】`;
        const areaName = x.name || "";
        const toiletTypeString = x.toiletType === undefined ? "" : this.toiletTypeToString(x.toiletType);
        return { name: `${floor}${toiletTypeString}${areaName}`, value: x.id || "" };
      });
    },
    createCutomBarGraphOptionsInAreAvgOverRoom(): ApexOptions {
      const selectedAreaName: string = this.selectedArea?.name || "";
      return {
        ...this.baseBarGraphOptions,
        title: {
          text: `${selectedAreaName} エリア平均`,
        },
      };
    },
    createCutomHeatmapOptionsInAreAvgOverRoom(): ApexOptions {
      const selectedAreaName: string = this.selectedArea?.name || "";
      return {
        ...this.baseHeatmapOptions,
        title: { text: `${selectedAreaName} エリア平均` },
      };
    },
    isToto() {
      return this.$globalData.companyId === "df10f697-270a-46b0-9b89-2a78be9afd6d";
    },
  },
  methods: {
    toiletTypeToString(name: string): string {
      switch (name) {
        case ToiletType.FOR_MAN:
          return "【男性】";
        case ToiletType.FOR_WOMAN:
          return "【女性】";
        case ToiletType.UNISEX:
          return "【共用】";
        case ToiletType.MULTI_PURPOSE:
          return "【多目的】";
        default:
          return "";
      }
    },
    async csvSelectorSelected(selectedOption: CsvDownloadOption): Promise<void> {
      if (selectedOption === "selectedAreaOnly") {
        if (this.selectedAreaId === undefined || this.selectedAreaId === "") {
          return;
        }
        const csvDownLoadContent: string = await fetchAnalyticsDataByAreaIdFormattedOnCsv(this.selectedAreaId);
        const floor: string = this.selectedArea?.floor || "";
        const areaName: string = this.selectedArea?.name || "";
        this.downloadAnalyticsDataAsCsv(`analytics-data-${floor}-${areaName}.csv`, csvDownLoadContent);
        return;
      }

      if (this.$auth.companyId === undefined) {
        return;
      }

      const csvDownLoadContent: string = await fetchAnalyticsDataFormattedOnCsv(this.$auth.companyId);
      this.downloadAnalyticsDataAsCsv("analytics-data-all.csv", csvDownLoadContent);
    },
    downloadAnalyticsDataAsCsv(csvFileName: string, csvDownLoadContent: string): void {
      const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
      const blob: Blob = new Blob([bom, csvDownLoadContent], { type: "text/csv" });
      if (window.navigator.msSaveBlob !== undefined) {
        // for IE
        window.navigator.msSaveOrOpenBlob(blob, csvFileName);
      } else {
        const link: HTMLAnchorElement = document.createElement("a");
        const objectUrl: string = window.URL.createObjectURL(blob);
        link.href = objectUrl;
        link.download = csvFileName;
        // we must do appendChild for firefox
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      }
    },
    async selectArea(areaId: string): Promise<void> {
      const companyId: string | undefined = this.$auth.companyId;
      if (companyId === undefined) {
        return;
      }
      this.selectedAreaId = areaId;
      const selectedArea: Toilet | undefined = this.areaList.find((x) => x.id === areaId);
      if (selectedArea === undefined) {
        throw Error("Unknown Error; selected area not found");
      }
      this.selectedArea = selectedArea;

      const { data, err } = await NewBoxesApiFunc().listBoxes(companyId, areaId, undefined);
      if (err != null) {
        return;
      }
      this.roomList = data.list;

      this.updateAverageGraph();
      this.renderGraphComponentWithEmptyData();

      const roomIdList: string[] = this.roomList.map((x) => x.id as string);
      this.updateEachRoomBarGraphAnalyticsData(roomIdList);
      this.updateEachRoomHeatmapAnalyticsData(roomIdList);
    },
    renderGraphComponentWithEmptyData(): void {
      this.barGraphAnalyticsDataInTheArea = this.roomList.map((room: Box) => {
        return { roomId: room.id as string, analyticsData: [{ data: [[0, 0]] }] };
      });
      this.heatmapAnalyticsDataInTheArea = this.roomList.map((room: Box) => {
        return { roomId: room.id as string, analyticsData: emptyHeatmapAnalyticsData };
      });
    },
    async updateAverageGraph(): Promise<void> {
      if (this.roomList.length <= 1) {
        // 個室が１つしかない時は平均出しても意味ないので。
        return;
      }
      this.barGraphAnalyticsAverageDataInTheArea = await fetchBarGraphAverageAnalyticsDataByAreaId(this.selectedAreaId);
      this.heatmapAnalyticsAverageDataInTheArea = await fetchHeatmapAverageAnalyticsDataByAreaId(this.selectedAreaId);
    },
    async updateEachRoomBarGraphAnalyticsData(roomIdList: string[]): Promise<void> {
      // barGraphAnalyticsDataInTheRoom のすべての roomId の分析データを一気に更新してしまうと、
      // ApexChart のレンダリングによりUIスレッドがブロックされてしまう。
      // そのため、 setTimeout を使って roomId ごとにタスク(この関数の中の処理)を分割してタスクキューに突っ込む時間をずらし、roomId １つずつ更新している。
      // Service Worker も考えたけど TypeScript とのインテグレーションができず断念...
      // よりよい書き方あれば修正してください。
      // see also: https://ja.javascript.info/event-loop
      if (roomIdList.length === 0) {
        return;
      }

      const [headRoomId, ...restRoomIdList] = roomIdList;
      const barGraphAnalyticsDataInTheRoom: { roomId: string; analyticsData: BarGraphAnalyticsData } | undefined =
        this.barGraphAnalyticsDataInTheArea.find((x) => x.roomId === headRoomId);

      if (barGraphAnalyticsDataInTheRoom === undefined) {
        return;
      }
      barGraphAnalyticsDataInTheRoom.analyticsData = await fetchBarGraphAnalyticsDataByRoomId(
        this.selectedAreaId,
        headRoomId,
      );

      setTimeout(async () => {
        this.updateEachRoomBarGraphAnalyticsData(restRoomIdList);
      }, renderingDelayTime);
    },
    async updateEachRoomHeatmapAnalyticsData(roomIdList: string[]): Promise<void> {
      if (roomIdList.length === 0) {
        return;
      }

      const [headRoomId, ...restRoomIdList] = roomIdList;
      const heatmapAnalyticsDataInTheRoom: { roomId: string; analyticsData: HeatmapAnalyticsData } | undefined =
        this.heatmapAnalyticsDataInTheArea.find((x) => x.roomId === headRoomId);

      if (heatmapAnalyticsDataInTheRoom === undefined) {
        return;
      }
      heatmapAnalyticsDataInTheRoom.analyticsData = await fetchHeatmapAnalyticsData(this.selectedAreaId, headRoomId);

      setTimeout(async () => {
        this.updateEachRoomHeatmapAnalyticsData(restRoomIdList);
      }, renderingDelayTime);
    },
    createCutomBarGraphOptions(roomName: string | undefined) {
      const selectedAreaName: string = this.selectedArea?.name || "";
      return {
        ...this.baseBarGraphOptions,
        title: {
          text: `${selectedAreaName} 個室名: ${roomName || ""}`,
        },
      };
    },
    createCutomHeatmapOptions(roomName: string | undefined) {
      const selectedAreaName: string = this.selectedArea?.name || "";
      return {
        ...this.baseHeatmapOptions,
        title: { text: `${selectedAreaName} 個室名: ${roomName || ""}` },
      };
    },
    barGraphSeries(roomId: string): { data: [number, number][] }[] {
      const barGraphAnalyticsDataInTheRoom: { roomId: string; analyticsData: BarGraphAnalyticsData } | undefined =
        this.barGraphAnalyticsDataInTheArea.find((x) => x.roomId === roomId);

      return barGraphAnalyticsDataInTheRoom === undefined
        ? [{ data: [[0, 0]] }]
        : barGraphAnalyticsDataInTheRoom.analyticsData;
    },
    heatmapSeries(roomId: string): { name: string; data: { x: string; y: number }[] }[] {
      const heatmapAnalyticsDataInTheRoom: { roomId: string; analyticsData: HeatmapAnalyticsData } | undefined =
        this.heatmapAnalyticsDataInTheArea.find((x) => x.roomId === roomId);

      return heatmapAnalyticsDataInTheRoom === undefined
        ? emptyHeatmapAnalyticsData
        : heatmapAnalyticsDataInTheRoom.analyticsData;
    },
  },
  async mounted() {
    if (this.$auth.companyId === undefined) {
      this.areaList = [];
      return;
    }
    const filter: string = JSON.stringify({ q: "", status: "" });

    // FIXME: ページネーションなしのやつがほしい。
    // const [res1, res2, res3, res4] = await Promise.all([
    //   NewToiletsApiFunc().listToilets(this.$auth.companyId, filter, 1),
    //   NewToiletsApiFunc().listToilets(this.$auth.companyId, filter, 2),
    //   NewToiletsApiFunc().listToilets(this.$auth.companyId, filter, 3),
    //   NewToiletsApiFunc().listToilets(this.$auth.companyId, filter, 4),
    // ]);
    const res1 = await NewToiletsApiFunc().listToilets(this.$auth.companyId, filter, 1);
    this.areaList = [...this.areaList, ...res1.data.list];
    const res2 = await NewToiletsApiFunc().listToilets(this.$auth.companyId, filter, 2);
    this.areaList = [...this.areaList, ...res2.data.list];
    const res3 = await NewToiletsApiFunc().listToilets(this.$auth.companyId, filter, 3);
    this.areaList = [...this.areaList, ...res3.data.list];
    const res4 = await NewToiletsApiFunc().listToilets(this.$auth.companyId, filter, 4);
    this.areaList = [...this.areaList, ...res4.data.list];
    // const { data, err }[]: ApiResponse<ListResponse<Toilet>>[] = await Promise.all([
    //   NewToiletsApiFunc().listToilets(
    //   this.$auth.companyId,
    //   filter,
    // ),
    // ])
    // if (res1.err != null || res2.err != null || res3.err != null || res4.err != null) {
    //   this.areaList = [];
    //   return;
    // }
    // this.areaList = [...res1.data.list, ...res2.data.list, ...res3.data.list, ...res4.data.list];

    this.staytimeSeriesList = await fetchStayTimeAnalyticsData(this.$auth.companyId);
  },
});
